import { c as _c } from "react/compiler-runtime";
import { Card, CardActions, CardBody, CardDescription, CardMetadata } from '@/components/Molecules/Card/Card';
import { AssetStatus } from '@/components/Molecules/AssetStatus/AssetStatus';
import { PolicyTypesLabel } from '@/components/Molecules/PolicyTypeLabel/PolicyTypeLabel';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement, IPolicySummary } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import type { ElementType } from 'react';
import { useId } from 'react';
import type { FCl } from 'react-fc';
import styles from './PolicyCard.module.css';
import { ContentStatus } from '@/components/Molecules/ContentStatus/ContentStatus';
export const PolicyCard: FCl<IPolicyCardProps> = t0 => {
  const $ = _c(44);
  const {
    analyticsBaseId,
    analyticsCategory: t1,
    className,
    headingLevel,
    minimal: t2,
    withSummary: t3,
    item
  } = t0;
  const analyticsCategory = t1 === undefined ? TrackingCategory.BnefContent : t1;
  const minimal = t2 === undefined ? false : t2;
  const withSummary = t3 === undefined ? false : t3;
  const {
    t
  } = useTranslation();
  const titleId = useId();
  let t4;
  if ($[0] !== item.markets || $[1] !== t) {
    t4 = item.markets?.length > 0 ? {
      label: t("common:Policies.labels.market"),
      value: item.markets.length > 1 ? t("Policies.multiple") : item.markets[0].name
    } : null;
    $[0] = item.markets;
    $[1] = t;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t) {
    t5 = t("common:Policies.labels.policyType");
    $[3] = t;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== item.types) {
    t6 = <PolicyTypesLabel types={item.types} />;
    $[5] = item.types;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = {
      label: t5,
      value: t6
    };
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = [t4, t7].filter(Boolean);
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const normalizedItems = t8;
  const showMetadata = !minimal && (item.markets?.length > 0 || item.types?.length > 0);
  const t9 = item.url;
  const t10 = item.title;
  let t11;
  if ($[13] !== item.status) {
    t11 = item.status && <AssetStatus status={item.status} />;
    $[13] = item.status;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== item.contentStatus) {
    t12 = <ContentStatus status={item.contentStatus} />;
    $[15] = item.contentStatus;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t11 || $[18] !== t12) {
    t13 = <div className={styles.headerLine}>{t11}{t12}</div>;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== analyticsBaseId || $[21] !== analyticsCategory) {
    t14 = buildTrackingObject(TrackingAction.FollowLink, analyticsCategory, analyticsBaseId);
    $[20] = analyticsBaseId;
    $[21] = analyticsCategory;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== item.summary || $[24] !== normalizedItems || $[25] !== showMetadata || $[26] !== withSummary) {
    t15 = (withSummary || showMetadata) && <CardBody>{withSummary && <CardDescription>{item.summary}</CardDescription>}{showMetadata && <CardMetadata items={normalizedItems} />}</CardBody>;
    $[23] = item.summary;
    $[24] = normalizedItems;
    $[25] = showMetadata;
    $[26] = withSummary;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== analyticsBaseId || $[29] !== item.id || $[30] !== item.title || $[31] !== item.type || $[32] !== titleId) {
    t16 = <CardActions><SaveButton aria-describedby={titleId} id={item.id} type={item.type} itemName={item.title} analyticsBaseId={analyticsBaseId} /></CardActions>;
    $[28] = analyticsBaseId;
    $[29] = item.id;
    $[30] = item.title;
    $[31] = item.type;
    $[32] = titleId;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== className || $[35] !== headingLevel || $[36] !== item.title || $[37] !== item.url || $[38] !== t13 || $[39] !== t14 || $[40] !== t15 || $[41] !== t16 || $[42] !== titleId) {
    t17 = <Card id={titleId} href={t9} headingLevel={headingLevel} className={className} title={t10} prefix={t13} data-analytics-tracking={t14}>{t15}{t16}</Card>;
    $[34] = className;
    $[35] = headingLevel;
    $[36] = item.title;
    $[37] = item.url;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = titleId;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
};
interface IPolicyCardProps extends IAnalyticsTraceableElement {
  headingLevel?: ElementType;
  item: IPolicySummary;
  minimal?: boolean;
  withSummary?: boolean;
}